"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/50 shadow-md rounded-lg flex flex-col animate-pulse bg-card">
            <AspectRatio ratio={16 / 9} className="bg-muted" />
            <CardContent className="p-6 flex-1 flex flex-col gap-4">
              <div className="h-6 bg-muted rounded-md w-3/4" />
              <div className="h-6 bg-muted rounded-md w-1/2" />
              <div className="mt-auto pt-4">
                <div className="h-4 bg-muted rounded-md w-24" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center bg-muted/30 rounded-lg border border-border border-dashed">
        <CircleAlert className="w-8 h-8 text-muted-foreground mb-4" />
        <p className="text-muted-foreground mb-4 font-sans">We couldn't load the latest financial insights at this time.</p>
        <button onClick={refetch} className="text-primary hover:underline font-default font-medium text-sm">
          Try again
        </button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 bg-muted/30 rounded-lg border border-border border-dashed">
        <p className="text-muted-foreground font-sans">New financial strategies are being prepared for you.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="overflow-hidden border-border/60 shadow-md hover:shadow-xl transition-shadow duration-300 rounded-lg flex flex-col bg-card group">
          <div className="relative overflow-hidden">
            <AspectRatio ratio={16 / 9}>
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684618/img/accounting_ledger_analysis.png"} alt={post.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
            </AspectRatio>
            <div className="absolute top-4 left-4">
              <Badge variant="secondary" className="bg-background/95 backdrop-blur-md text-foreground flex items-center gap-1.5 shadow-sm border-none font-mono text-xs">
                <SafeHydrate><Calendar className="w-3 h-3 text-secondary" /></SafeHydrate>
                {post.date ? new Date(post.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }) : "Recent"}
              </Badge>
            </div>
          </div>
          <CardContent className="p-6 flex-1 flex flex-col">
            <h3 className="font-default text-xl font-semibold mb-3 text-foreground leading-tight line-clamp-2 tracking-tight">
              {post.title}
            </h3>
            {post.excerpt && <p className="text-muted-foreground font-sans text-sm line-clamp-2 mb-4" dangerouslySetInnerHTML={{
          __html: post.excerpt
        }} />}
            <div className="mt-auto pt-4">
              <Link to={post.link || "#"} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-secondary transition-colors group/link">
                <span className="underline underline-offset-4 decoration-primary/30 group-hover/link:decoration-secondary/80 transition-colors font-default">
                  Read Analysis
                </span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1 text-secondary" />
              </Link>
            </div>
          </CardContent>
        </Card>)}
    </div>;
}
export default function GrowthInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="222687" id="growth-insights" className="bg-white text-foreground py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
              Growth Insights
            </h2>
            <p className="text-muted-foreground font-sans text-lg">
              Expert financial guidance, tax strategies, and bookkeeping best practices to scale your business with precision.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/ResourcesPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-primary bg-transparent text-primary shadow-sm hover:bg-primary hover:text-primary-foreground h-10 px-6 font-default">
              View All Resources
            </Link>
          </div>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}